import SharedPreferences from "../../constants/SharedPreferences";
import {
  getErrorMessage,
  saveInStorage,
  logApiResponse,
} from "../../util/Utility";
import {
  getTwoFARestClient,
  getNonAuthRestClientForAdmin,
} from "../RestUtility";
import Constants from "../../constants/Constants";
import BayunError from "../../constants/BayunError";
import { AuthenticateResponse } from "../../BayunCore";
import { validateSecurityQuestionsPassphraseDialog } from "./internal/twoFaDialogUi";

const isPassphraseActiveFlag = (value) =>
  value === true ||
  value === "true" ||
  (typeof value === "string" && value.toLowerCase() === "true");

export async function getTwoFAData(
  sessionId,
  bayunCore,
  appId,
  appSecret,
  passcode,
  successCallback,
  failureCallback,
  securityQuestionsCallback,
  passphraseCallback,
  authorizeMemberCallback,
) {
  let restClient;
  let getTwoFADataResponse;

  if (process.env.SOURCE == Constants.SOURCE.BAYUN_ADMIN) {
    // create a 2FA rest client
    restClient = getNonAuthRestClientForAdmin(sessionId);
    // call the getTwoFAData API
    getTwoFADataResponse = await restClient.get("/admin/twofa/twoFAData");
  } else {
    // create a 2FA rest client
    restClient = getTwoFARestClient(appId, sessionId);
    // call the getTwoFAData API
    getTwoFADataResponse = await restClient.get("/twofa/twoFAData");
  }

  await logApiResponse("getTwoFADataResponse", getTwoFADataResponse);

  try {
    const userRecoveryStatus = getTwoFADataResponse.data.userRecoveryStatus;

    // Recovery already initiated by an admin — do not enter recovery/2FA UI
    if (
      userRecoveryStatus === Constants.USER_RECOVERY_STATUS.RECOVERY_STARTED
    ) {
      failureCallback(BayunError.ERROR_USER_RECOVERY_ALREADY_IN_PROGRESS);
      return;
    }

    await saveInStorage(sessionId, SharedPreferences.IS_TWO_FA_ENABLED, true);

    const answerAuthSalt = getTwoFADataResponse.data.answerAuthSalt;
    const securityQuestions = getTwoFADataResponse.data.securityQuestions;
    const isPassphraseActive = getTwoFADataResponse.data.isPassphraseActive;
    const hasRecoveryKeyAndKek = getTwoFADataResponse.data.hasRecoveryKeyAndKek;

    // save the data
    await saveInStorage(
      sessionId,
      SharedPreferences.USER_AUTH_SALT,
      getTwoFADataResponse.data.userAuthSalt,
    );
    await saveInStorage(
      sessionId,
      SharedPreferences.IS_PASSPHRASE_ACTIVE,
      isPassphraseActive,
    );

    // A recovery-mode response carries only the recovery fields, so overwriting unconditionally
    // would erase the email captured at login and leave the user-scoped signing contexts
    // ("userPublicKey$<email>") unresolvable on the server.
    if (getTwoFADataResponse.data.email) {
      await saveInStorage(
        sessionId,
        SharedPreferences.EMAIL_ADDRESS,
        getTwoFADataResponse.data.email,
      );
    }

    await saveInStorage(
      sessionId,
      SharedPreferences.RECOVERY_FLOW,
      userRecoveryStatus,
    );

    await saveInStorage(
      sessionId,
      SharedPreferences.ANSWER_AUTH_SALT,
      JSON.stringify(answerAuthSalt),
    );

    await saveInStorage(
      sessionId,
      SharedPreferences.SECURITY_QUESTIONS,
      JSON.stringify(securityQuestions),
    );

    if (getTwoFADataResponse.data.minimumCorrectQuestions != null) {
      await saveInStorage(
        sessionId,
        SharedPreferences.MINIMUM_CORRECT_QUESTIONS,
        String(getTwoFADataResponse.data.minimumCorrectQuestions),
      );
    }

    const validationSecretDataRequest =
      getTwoFADataResponse.data.validationSecretDataRequest;
    if (validationSecretDataRequest?.length) {
      await saveInStorage(
        sessionId,
        SharedPreferences.VALIDATION_SECRET_DATA_REQUEST,
        JSON.stringify(validationSecretDataRequest),
      );
    }

    if (passphraseCallback == null && securityQuestionsCallback == null) {
      // if (showUIForTwoFA) {
      // show the UI for passphrase and/or security questions
      validateSecurityQuestionsPassphraseDialog(
        sessionId,
        bayunCore,
        securityQuestions,
        isPassphraseActive,
        getTwoFADataResponse.data.minimumCorrectQuestions,
        passcode, // todo: when no passcode exist
        authorizeMemberCallback,
        successCallback,
        failureCallback,
        userRecoveryStatus,
      );
    } else {
      // return the data for the developer to show the UI
      //if (isPassphraseActive || isPassphraseActive === "true") {

      if (
        getTwoFADataResponse.data.userRecoveryStatus ===
        Constants.USER_RECOVERY_STATUS.SET_CREDENTIALS
      ) {
        // TODO: reject this on the third-party (BASE_KIT) path instead of inviting a callback that
        // cannot be serviced.
        //
        // Recovery completion needs `updateUserRecoveryFlow` (and the other members assigned in
        // BayunCore's BAYUN_ADMIN-only constructor branch, BayunCore.js:255-266). On BASE_KIT those
        // methods are never assigned, so they do not exist on the instance. A consumer that supplies
        // its own passphraseCallback is therefore told "verify your passphrase", collects it from the
        // user, and then has no API to finish with - a silent dead end rather than an error.
        //
        // The SDK's own dialog already handles this honestly for the no-callback case: it shows
        // "This account is currently in recovery mode. Please use the Bayun Admin Panel to complete
        // account recovery." (twoFaDialogUi.js:437-452). This branch should say the same thing
        // programmatically, e.g. failureCallback(BayunError.ERROR_...RECOVERY_NOT_SUPPORTED) when
        // process.env.SOURCE !== Constants.SOURCE.BAYUN_ADMIN.
        //
        // Not changed here because it turns a silent no-op into a thrown error for any existing
        // consumer that has built a recovery UI against this callback, which is an API-visible
        // change and wants its own release note.
        passphraseCallback({
          sessionId,
          authenticationResponse: AuthenticateResponse.VERIFY_PASSPHRASE,
          hasRecoveryKeyAndKek,
        });
      } else {
        if (
          isPassphraseActiveFlag(isPassphraseActive)
        ) {
          passphraseCallback({
            sessionId,
            securityQuestions,
            minimumCorrectQuestions: getTwoFADataResponse.data.minimumCorrectQuestions,
            authenticationResponse: AuthenticateResponse.VERIFY_PASSPHRASE,
            hasRecoveryKeyAndKek,
          });
        } else {
          securityQuestionsCallback({
            sessionId,
            securityQuestions,
            minimumCorrectQuestions: getTwoFADataResponse.data.minimumCorrectQuestions,
            authenticationResponse:
              AuthenticateResponse.VERIFY_SECURITY_QUESTIONS,
            hasRecoveryKeyAndKek,
          });
        }
      }
    }
  } catch (error) {
    console.log(error);
    failureCallback(getErrorMessage(sessionId, error?.response?.data ?? error));
  }
}
